import { useFeatureGate } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { FC } from 'react';

import { Body, Button, Menu, MenuItem, MenuToggleButton, Subhead, Tag, useRebrand } from '@growtherapy/sprout-ui';

import { AppointmentTime, type AppointmentTimeProps } from '../../components/AppointmentTime';
import { telehealthAdmitStatusIndicatorGate } from '../../statsig/gates';
import { VisitorPresence, VisitorPresenceMap } from '../../types';
import { VideoPlatform } from '../../video-sdk';
import { JoiningClientLoader } from '../admission/JoiningClientLoader';
import { useAdmittedClientStatus } from '../admission/useAdmittedClientStatus';
import { hasNewFormsAtomFamily } from '../client-information/state';
import { ClientActions } from './ClientActions';
import { ClientActionsMenu } from './ClientActionsMenu';

export type ClientQueueActionsProps = {
  appointment?: AppointmentTimeProps['appointment'];
  currentSessionPatientShortId: VisitorPresence['patientShortId'] | null;
  inSession?: boolean;
  isAppointmentLoading?: AppointmentTimeProps['isLoading'];
  isConversationLoading?: boolean;
  isClientInformationLoading?: boolean;
  /**
   * The client user cannot be admitted because the video platform that they're
   * using is incompatible with the provider's video platform and it's not
   * possible for either party to switch platforms.
   */
  isIncompatible?: boolean;
  isZoom?: boolean;
  loading?: boolean;
  onChat?: () => void;
  onOpenClientInformationDrawer: () => void;
  onAdmit: () => void;
  onShowStartNewSessionConfirmation: () => void;
  unreadMessagesCount?: number;
  visitorName: string;
  visitorUuid: string;
  patientShortId: string;
  joinRoomMode?: VisitorPresence['joinRoomMode'];
};

type ClientQueueActionButtonsProps = Pick<
  ClientQueueActionsProps,
  | 'currentSessionPatientShortId'
  | 'inSession'
  | 'isIncompatible'
  | 'isZoom'
  | 'loading'
  | 'onAdmit'
  | 'onShowStartNewSessionConfirmation'
  | 'patientShortId'
  | 'visitorName'
>;

function ClientQueueActionButtons({
  currentSessionPatientShortId,
  inSession,
  isIncompatible,
  isZoom,
  loading,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  visitorName,
}: ClientQueueActionButtonsProps) {
  const isRebrand = useRebrand();
  const isRequestForNewSession =
    isIncompatible || (!!currentSessionPatientShortId && currentSessionPatientShortId !== patientShortId);
  const admitLabel = isZoom ? 'Invite in' : 'Admit';

  if (isRequestForNewSession) {
    return (
      <Button
        data-testid={`client-actions.admit:${visitorName}.end-and-start-new-session`}
        loading={loading}
        onClick={onShowStartNewSessionConfirmation}
        size='sm'
        use='secondary'
      >
        {admitLabel}
      </Button>
    );
  }

  if (inSession) {
    return (
      <Menu
        placement='bottom end'
        buttonClassName='py-3 h-12'
        toggleButton={
          <MenuToggleButton
            data-testid={`client-actions.menu:${visitorName}`}
            use={isRebrand ? 'secondary' : 'primary'}
            loading={loading}
          >
            {admitLabel}
          </MenuToggleButton>
        }
      >
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.admit-to-current-session`}
          onClick={() => {
            onAdmit();
          }}
        >
          {isZoom ? 'Invite in to current session' : 'Admit to current session'}
        </MenuItem>
        <MenuItem
          data-testid={`client-actions.menu:${visitorName}.end-and-start-new-session`}
          onClick={() => onShowStartNewSessionConfirmation()}
        >
          End and start new session
        </MenuItem>
      </Menu>
    );
  }

  return (
    <Button
      use='secondary'
      size='sm'
      data-testid={`client-actions.admit:${visitorName}`}
      loading={loading}
      onClick={onAdmit}
    >
      {admitLabel}
    </Button>
  );
}

export function ClientQueueActions({
  appointment,
  currentSessionPatientShortId,
  inSession,
  isAppointmentLoading,
  isConversationLoading,
  isIncompatible,
  isZoom,
  loading,
  onChat,
  onOpenClientInformationDrawer,
  onAdmit,
  onShowStartNewSessionConfirmation,
  patientShortId,
  unreadMessagesCount,
  visitorName,
  visitorUuid,
  joinRoomMode,
}: ClientQueueActionsProps) {
  const isRequestForNewSession =
    isIncompatible || (!!currentSessionPatientShortId && currentSessionPatientShortId !== patientShortId);

  const clientNameAnnotation = isRequestForNewSession ? (
    <AppointmentTime appointment={appointment} isLoading={isAppointmentLoading} variant='xs' />
  ) : null;

  const { value: granularStatusEnabled } = useFeatureGate(telehealthAdmitStatusIndicatorGate);
  const showGranularLoader = loading && granularStatusEnabled;
  // gate off: manual join still gets the legacy two-state loader, auto-join keeps the spinning Admit button
  const showLegacyLoader = loading && !granularStatusEnabled && joinRoomMode === 'manual';

  const admittedClientStatus = useAdmittedClientStatus({ visitorUuid, granularStatusEnabled });
  const hasNewForms = useAtomValue(hasNewFormsAtomFamily(patientShortId));

  return (
    <ClientActions
      clientName={visitorName}
      clientNameAnnotation={clientNameAnnotation}
      actions={
        showGranularLoader ? (
          <JoiningClientLoader admittedClientStatus={admittedClientStatus} onDarkSurface visitorName={visitorName} />
        ) : showLegacyLoader ? (
          <JoiningClientLoader />
        ) : (
          <div className='flex gap-2 shrink-0'>
            <ClientQueueActionButtons
              currentSessionPatientShortId={currentSessionPatientShortId}
              inSession={inSession}
              isIncompatible={isIncompatible}
              isZoom={isZoom}
              loading={loading}
              onAdmit={onAdmit}
              onShowStartNewSessionConfirmation={onShowStartNewSessionConfirmation}
              patientShortId={patientShortId}
              visitorName={visitorName}
            />
            {/* TODO: JRNY-1299 */}
            {/* <Button use="destructive">Deny</Button> */}
            {onChat && (
              <ClientActionsMenu
                visitorName={visitorName}
                loading={loading}
                isConversationLoading={isConversationLoading}
                patientShortId={patientShortId}
                unreadMessagesCount={unreadMessagesCount}
                onChat={onChat}
                onOpenClientInformationDrawer={onOpenClientInformationDrawer}
                hasNewClientInfo={hasNewForms}
              />
            )}
          </div>
        )
      }
    />
  );
}

type ClientQueueProps = {
  clients: VisitorPresenceMap;
  clientActionsComponent: FC<
    Pick<ClientQueueActionsProps, 'visitorName' | 'patientShortId' | 'joinRoomMode'> & {
      visitorUuid: string;
      visitorVideoPlatform?: VideoPlatform | null;
      providerVideoPlatform?: VideoPlatform | null;
    }
  >;
  onAdmit?: (uuid: string, name: string) => void;
  loading?: boolean;
  providerVideoPlatform?: VideoPlatform | null;
};

export function ClientQueue({
  clients,
  clientActionsComponent: ClientActionsComponent,
  providerVideoPlatform,
}: ClientQueueProps) {
  const isRebrand = useRebrand();

  const hasWaitingClients = Object.keys(clients).length;
  const numClientsInQueue = Object.keys(clients).length ?? 0;

  return (
    <>
      <div className='flex items-center gap-2'>
        <Subhead.Small as='h3' className='font-medium text-medium rebrand:text-text-inverse'>
          Client queue
        </Subhead.Small>
        <Tag
          aria-label={`${numClientsInQueue} waiting`}
          use={isRebrand ? 'lilac' : 'mauve'}
          data-testid='client-queue.count'
          className='rebrand:ring-0'
        >
          {numClientsInQueue}
        </Tag>
      </div>
      {hasWaitingClients ? (
        Object.entries(clients).map(
          ([
            visitorUuid,
            { name: visitorName = '', patientShortId, videoPlatform = VideoPlatform.Twilio, joinRoomMode },
          ]) => (
            <ClientActionsComponent
              key={visitorUuid}
              {...{
                visitorName,
                visitorUuid,
                patientShortId,
                visitorVideoPlatform: videoPlatform,
                providerVideoPlatform,
                joinRoomMode,
              }}
            />
          ),
        )
      ) : (
        <Body.Default className='rebrand:text-text-inverse'>
          Clients who join your waiting room will appear here.
        </Body.Default>
      )}
    </>
  );
}
